import React from 'react';

import { Button, Grid } from '@mui/material';
import { FormState } from 'final-form';
import { Field, Form, FormSpy } from 'react-final-form';

import { GET_IN_TOUCH_DATA_FIELDS, GET_IN_TOUCH_REQUEST_TYPES } from '@clearverify/constants';

import {
  useDataDisputeItemsListLazyQuery,
  useSendGetHelpToClearVerifyStaffMutation,
  useVerificationRequestsListLazyQuery,
} from 'src/api';
import { AttachFilesUploadField, SelectField, TextField } from 'src/components/finalForm';
import { useNotification } from 'src/hooks/app';
import { useScreenInfo } from 'src/hooks/common';
import { useCurrentUser, useCurrentUserPermissions } from 'src/hooks/user';
import { SxProp } from 'src/types/mui';
import { getBusinessRolesId } from 'src/utils/role';
import { validateWithYupSchema } from 'src/utils/yup';

import { GetInTouchFormValues } from './types';
import { REQUEST_TYPES_OPTIONS, VERIFIER_TYPES_OPTIONS } from './utils/constants';
import {
  formatDataDisputeItemsOptions,
  formatVerificationRequestOptions,
  getDataDisputeItemsListVariablesByRole,
  getVerificationRequestVariablesByRole,
  prepareGetInTouchFormData,
} from './utils/form';
import { getInTouchDataValidationSchema } from './utils/validation';

type GetInTouchFormProps = {
  handleCloseModal: () => void;
};

export const GetInTouchForm: React.FC<GetInTouchFormProps> = ({ handleCloseModal }) => {
  const { isMobile } = useScreenInfo();
  const { spawnNotification, createApolloContext } = useNotification();
  const userPermissions = useCurrentUserPermissions();
  const { user } = useCurrentUser();
  const [sendGetHelpToClearVerifyStaff, { loading: sending }] =
    useSendGetHelpToClearVerifyStaffMutation({
      context: createApolloContext({
        successMessage: 'Successfully sent',
      }),
    });

  const businessRolesIds = getBusinessRolesId(user, userPermissions);

  const [
    getVerificationRequestList,
    { data: verificationRequestsData, loading: verificationRequestDataLoading },
  ] = useVerificationRequestsListLazyQuery({
    ...getVerificationRequestVariablesByRole(businessRolesIds, userPermissions),
  });

  const [
    getDataDisputeItemsList,
    { data: dataDisputeItemsListData, loading: dataDisputeDataLoading },
  ] = useDataDisputeItemsListLazyQuery({ ...getDataDisputeItemsListVariablesByRole });

  const verificationRequestOptions = React.useMemo(
    () => formatVerificationRequestOptions(verificationRequestsData, userPermissions),
    [userPermissions, verificationRequestsData],
  );

  const dataDisputeItemsOptions = React.useMemo(
    () => formatDataDisputeItemsOptions(dataDisputeItemsListData, userPermissions),
    [dataDisputeItemsListData, userPermissions],
  );

  const requestTypeOptions = React.useMemo(() => {
    return userPermissions.hasVerifierRoles ? VERIFIER_TYPES_OPTIONS : REQUEST_TYPES_OPTIONS;
  }, [userPermissions]);

  const handleRequestTypeChange = ({ values }: FormState<GetInTouchFormValues>) => {
    if (!businessRolesIds) {
      spawnNotification("Can't determine business role id", 'error');
      return;
    }

    if (values.requestType === GET_IN_TOUCH_REQUEST_TYPES.verificationRequest) {
      getVerificationRequestList();
      return;
    }

    if (values.requestType === GET_IN_TOUCH_REQUEST_TYPES.dataDispute) {
      getDataDisputeItemsList();
    }
  };

  const loading = sending || verificationRequestDataLoading || dataDisputeDataLoading;

  const onSubmit = React.useCallback(
    async (data: GetInTouchFormValues) => {
      const preparedData = prepareGetInTouchFormData(data, user);
      try {
        await sendGetHelpToClearVerifyStaff({ variables: { data: preparedData } });
        handleCloseModal();
      } catch (error) {
        console.error(error);
      }
    },
    [handleCloseModal, sendGetHelpToClearVerifyStaff, user],
  );

  return (
    <Form<GetInTouchFormValues>
      onSubmit={onSubmit}
      subscription={{ valid: true, values: true, submitting: true, errors: true }}
      validate={validateWithYupSchema(getInTouchDataValidationSchema)}
    >
      {({ values, valid, submitting }) => (
        <Grid container rowSpacing={2}>
          <Grid size={12}>
            <Field
              label="Request Type"
              name={GET_IN_TOUCH_DATA_FIELDS.requestType}
              options={requestTypeOptions}
              fullWidth
              required
              component={SelectField}
            />
          </Grid>
          {(values.requestType === GET_IN_TOUCH_REQUEST_TYPES.verificationRequest ||
            values.requestType === GET_IN_TOUCH_REQUEST_TYPES.dataDispute) && (
            <Grid size={12}>
              <Field
                label={
                  values.requestType === GET_IN_TOUCH_REQUEST_TYPES.verificationRequest
                    ? 'Select Verification Request'
                    : 'Select Data Dispute'
                }
                name={
                  values.requestType === GET_IN_TOUCH_REQUEST_TYPES.verificationRequest
                    ? GET_IN_TOUCH_DATA_FIELDS.verificationRequest
                    : GET_IN_TOUCH_DATA_FIELDS.dataDispute
                }
                options={
                  values.requestType === GET_IN_TOUCH_REQUEST_TYPES.verificationRequest
                    ? verificationRequestOptions
                    : dataDisputeItemsOptions
                }
                fullWidth
                component={SelectField}
              />
            </Grid>
          )}

          <Grid mb={isMobile ? 3 : 0} size={12}>
            <Grid sx={feedbackFieldWrapperStyles} size={12}>
              <Field
                label="Share your Feedback"
                name={GET_IN_TOUCH_DATA_FIELDS.description}
                multiline
                minRows={3}
                component={TextField}
                fullWidth
              />
            </Grid>
            <Grid mt={2} alignContent="end" justifyContent="end" size={12}>
              <Field
                name={GET_IN_TOUCH_DATA_FIELDS.attachments}
                component={AttachFilesUploadField}
                acceptFileTypes={[]}
                disabled={submitting}
              />
            </Grid>
          </Grid>
          <Grid container alignItems="center" justifyContent="center" spacing={2}>
            <Grid>
              <Button variant="outlined" size="large" color="error" onClick={handleCloseModal}>
                CANCEL
              </Button>
            </Grid>
            <Grid>
              <Button
                type="submit"
                variant="contained"
                size="large"
                loading={loading}
                disabled={!valid || submitting}
              >
                Submit
              </Button>
            </Grid>
          </Grid>
          <FormSpy onChange={handleRequestTypeChange} subscription={{ values: true }} />
        </Grid>
      )}
    </Form>
  );
};

const feedbackFieldWrapperStyles: SxProp = theme => ({
  [theme.breakpoints.down('sm')]: {
    position: 'relative',
  },
});
